import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faEye, 
  faEdit, 
  faTrashAlt, 
  faArrowUp, 
  faArrowDown,
  faExclamationTriangle,
  faCalendarAlt,
  faDownload,
  faList,
  faThList
} from '@fortawesome/free-solid-svg-icons';
import AdminLayout from './AdminLayout';
import { ADMIN_URLS, getResourceUrl } from '../../config/apiConfig';

function OfficeBearersList() {
  const navigate = useNavigate();
  const [officeBearers, setOfficeBearers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({ position: '', year: '' });
  const [yearOptions, setYearOptions] = useState([]);
  const [totalBearers, setTotalBearers] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bearerToDelete, setBearerToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [sessionAlert, setSessionAlert] = useState(false);
  const [viewMode, setViewMode] = useState('year-wise'); // 'year-wise' or 'list'
  const [groupedBearers, setGroupedBearers] = useState({});
  
  // Handle authentication errors
  const handleAuthError = () => {
    setSessionAlert(true);
    setTimeout(() => {
      navigate('/admin/login', { replace: true });
    }, 2000);
  };
  
  const getValidToken = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      handleAuthError();
      return null;
    }
    return token;
  };

  // Fetch available years for filtering
  const fetchYearOptions = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      
      const response = await fetch(`${ADMIN_URLS.OFFICE_BEARERS}/years`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to fetch year options');
      }
      
      const data = await response.json();
      setYearOptions(data.years || []);
    } catch (error) {
      console.error('Error fetching year options:', error);
      // Use current year and a few previous years as fallback
      const currentYear = new Date().getFullYear();
      const fallbackYears = Array.from({length: 5}, (_, i) => currentYear - i);
      setYearOptions(fallbackYears);
    }
  };
  
  const fetchOfficeBearers = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = getValidToken();
      if (!token) return;
      
      let url = `${ADMIN_URLS.OFFICE_BEARERS}?all=true`;
      
      if (searchTerm) {
        url += `&name=${encodeURIComponent(searchTerm)}`;
      }
      
      if (filter.position) {
        url += `&position=${encodeURIComponent(filter.position)}`;
      }
      
      if (filter.year) {
        url += `&year=${encodeURIComponent(filter.year)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to fetch office bearers');
      }
      
      const data = await response.json();
      const bearers = data.items || data;
      setOfficeBearers(bearers);
      setTotalBearers(bearers.length);
      
      // Group bearers by year for year-wise view
      groupBearersByYear(bearers);
    } catch (error) {
      console.error('Error fetching office bearers:', error);
      setError(error.message);
      setOfficeBearers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      handleAuthError();
      return;
    }
    
    fetchOfficeBearers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchTerm]);
  
  useEffect(() => {
    fetchYearOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchOfficeBearers();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
  };
  
  const handleDelete = (bearer) => {
    setBearerToDelete(bearer);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    const token = getValidToken();
    if (!token) return;
    
    try {
      const response = await fetch(`${ADMIN_URLS.OFFICE_BEARER_DETAIL(bearerToDelete.id)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to delete office bearer');
      }
      
      setShowDeleteModal(false);
      setBearerToDelete(null);
      
      // Refresh the list after deletion
      fetchOfficeBearers();
    } catch (error) {
      console.error('Error deleting office bearer:', error);
      alert(`Failed to delete office bearer: ${error.message}`);
      setShowDeleteModal(false);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setBearerToDelete(null);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const handleChangeOrder = async (bearerId, direction) => {
    const token = getValidToken();
    if (!token) return;
    
    try {
      const response = await fetch(`${ADMIN_URLS.OFFICE_BEARER_REORDER(bearerId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ direction })
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to reorder office bearers');
      }
      
      // Refresh the list
      fetchOfficeBearers();
    } catch (error) {
      console.error('Error reordering office bearers:', error);
      alert(`Failed to change order: ${error.message}`);
    }
  };

  // Group office bearers by year
  const groupBearersByYear = (bearers) => {
    const grouped = {};
    
    bearers.forEach(bearer => {
      let year = 'Unknown';
      
      if (bearer.term_start) {
        year = new Date(bearer.term_start).getFullYear();
      } else if (bearer.term_end) {
        year = new Date(bearer.term_end).getFullYear();
      }
      
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(bearer);
    });
    
    // Sort years in descending order
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'Unknown') return 1;
        if (b === 'Unknown') return -1;
        return parseInt(b) - parseInt(a);
      })
      .forEach(year => {
        sortedGrouped[year] = grouped[year];
      });
    
    setGroupedBearers(sortedGrouped);
  };

  // Export functionality
  const handleExport = async (format = 'csv') => {
    const token = getValidToken();
    if (!token) return;

    try {
      let url = `${ADMIN_URLS.OFFICE_BEARERS}/export?format=${format}`;
      
      if (filter.year) {
        url += `&year=${encodeURIComponent(filter.year)}`;
      }
      if (filter.position) {
        url += `&position=${encodeURIComponent(filter.position)}`;
      }
      if (searchTerm) {
        url += `&name=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      // Get filename from response headers or create default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `office-bearers-${new Date().toISOString().split('T')[0]}.${format}`;
      
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) {
          filename = matches[1];
        }
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (error) {
      console.error('Error exporting data:', error);
      alert(`Failed to export data: ${error.message}`);
    }
  };

  return (
    <AdminLayout>
      {sessionAlert && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '1rem',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          maxWidth: '90%',
          width: '400px'
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Your session has expired. Redirecting to login...
        </div>
      )}
      
      <div className="admin-page-header">
        <h1 className="admin-page-title">Office Bearers</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Office Bearers</span>
          </li>
        </ul>
      </div>
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            Manage Office Bearers
            {totalBearers > 0 && (
              <span style={{ 
                fontSize: '0.85rem', 
                fontWeight: 'normal', 
                color: 'var(--admin-text-muted)',
                marginLeft: '0.5rem'
              }}>
                ({totalBearers} total)
              </span>
            )}
          </h2>
        </div>
        <div className="admin-card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            {/* Search form */}
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexGrow: 1, maxWidth: '700px' }}>
              <div style={{ position: 'relative', flexGrow: 1 }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name..."
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.5rem 0.5rem 2rem',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                  }}
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  style={{
                    position: 'absolute',
                    left: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              
              <select
                name="position"
                value={filter.position}
                onChange={handleFilterChange}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                }}
              >
                <option value="">All Positions</option>
                <option value="President">President</option>
                <option value="Vice President">Vice President</option>
                <option value="Secretary">Secretary</option>
                <option value="Treasurer">Treasurer</option>
                <option value="Joint Secretary">Joint Secretary</option>
                <option value="Executive Member">Executive Member</option>
              </select>
              
              <div style={{ position: 'relative' }}>
                <select
                  name="year"
                  value={filter.year}
                  onChange={handleFilterChange}
                  style={{
                    padding: '0.5rem 0.5rem 0.5rem 2rem',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    appearance: 'auto',
                  }}
                >
                  <option value="">All Years</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  style={{
                    position: 'absolute',
                    left: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    pointerEvents: 'none'
                  }}
                />
              </div>
              
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {/* View Mode Toggle */}
              <div style={{ display: 'flex', gap: '0.25rem', backgroundColor: '#f8f9fa', borderRadius: '4px', padding: '2px' }}>
                <button
                  className={`admin-btn ${viewMode === 'year-wise' ? 'admin-btn-primary' : 'admin-btn-light'}`}
                  style={{ 
                    padding: '0.4rem 0.8rem',
                    fontSize: '0.85rem',
                    margin: 0,
                    border: 'none'
                  }}
                  onClick={() => setViewMode('year-wise')}
                  title="Year-wise view"
                >
                  <FontAwesomeIcon icon={faThList} style={{ marginRight: '0.3rem' }} />
                  Year-wise
                </button>
                <button
                  className={`admin-btn ${viewMode === 'list' ? 'admin-btn-primary' : 'admin-btn-light'}`}
                  style={{ 
                    padding: '0.4rem 0.8rem',
                    fontSize: '0.85rem',
                    margin: 0,
                    border: 'none'
                  }}
                  onClick={() => setViewMode('list')}
                  title="List view"
                >
                  <FontAwesomeIcon icon={faList} style={{ marginRight: '0.3rem' }} />
                  List
                </button>
              </div>
              
              {/* Export Dropdown */}
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className="admin-btn admin-btn-success"
                  style={{ 
                    padding: '0.5rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={() => handleExport('csv')}
                  title="Export as CSV"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Export CSV
                </button>
              </div>
              
              <Link to="/admin/office-bearers/add" className="admin-btn admin-btn-primary">
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
                Add Office Bearer
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-body p-0">
          {isLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Loading office bearers data...</div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--admin-danger)' }}>
              Error: {error}
            </div>
          ) : (
            <>
              {viewMode === 'year-wise' ? (
                // Year-wise grouped view
                <div>
                  {Object.keys(groupedBearers).length > 0 ? (
                    Object.entries(groupedBearers).map(([year, bearers]) => (
                      <div key={year} style={{ marginBottom: '2rem' }}>
                        <div style={{ 
                          backgroundColor: '#f8f9fa', 
                          padding: '1rem', 
                          borderBottom: '2px solid var(--admin-primary)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <h3 style={{ margin: 0, color: 'var(--admin-primary)' }}>
                            {year === 'Unknown' ? 'Term Not Specified' : `Year ${year}`}
                          </h3>
                          <span style={{ 
                            backgroundColor: 'var(--admin-primary)', 
                            color: 'white', 
                            padding: '0.25rem 0.75rem', 
                            borderRadius: '12px',
                            fontSize: '0.85rem'
                          }}>
                            {bearers.length} bearer{bearers.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        <div className="admin-table-responsive">
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Image</th>
                                <th>Name</th>
                                <th>Position</th>
                                <th>Term</th>
                                <th>Order</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bearers.map((bearer, index) => (
                                <tr key={bearer.id}>
                                  <td>
                                    <div style={{
                                      width: '50px',
                                      height: '50px',
                                      borderRadius: '50%',
                                      backgroundImage: `url(${getResourceUrl(bearer.image_url)})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundColor: '#e9ecef',
                                      backgroundRepeat: 'no-repeat'
                                    }}></div>
                                  </td>
                                  <td>{bearer.name}</td>
                                  <td>{bearer.position}</td>
                                  <td>
                                    {bearer.term_start ? (
                                      <>
                                        {formatDate(bearer.term_start)} 
                                        {bearer.term_end ? ' - ' + formatDate(bearer.term_end) : ' - Present'}
                                      </>
                                    ) : 'N/A'}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                      <button
                                        onClick={() => handleChangeOrder(bearer.id, 'up')}
                                        className="admin-table-btn"
                                        disabled={index === 0}
                                        title="Move up"
                                      >
                                        <FontAwesomeIcon icon={faArrowUp} />
                                      </button>
                                      <button
                                        onClick={() => handleChangeOrder(bearer.id, 'down')}
                                        className="admin-table-btn"
                                        disabled={index === bearers.length - 1}
                                        title="Move down"
                                      >
                                        <FontAwesomeIcon icon={faArrowDown} />
                                      </button>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="admin-table-actions">
                                      <Link 
                                        to={`/admin/office-bearers/${bearer.id}`} 
                                        className="admin-table-btn admin-table-btn-view"
                                        title="View office bearer details"
                                      >
                                        <FontAwesomeIcon icon={faEye} />
                                      </Link>
                                      <Link 
                                        to={`/admin/office-bearers/${bearer.id}/edit`} 
                                        className="admin-table-btn admin-table-btn-edit"
                                        title="Edit office bearer"
                                      >
                                        <FontAwesomeIcon icon={faEdit} />
                                      </Link>
                                      <button 
                                        className="admin-table-btn admin-table-btn-delete"
                                        title="Delete office bearer"
                                        onClick={() => handleDelete(bearer)}
                                      >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                      No office bearers found matching your criteria
                    </div>
                  )}
                </div>
              ) : (
                // Regular list view
                <div className="admin-table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Position</th>
                        <th>Term</th>
                        <th>Order</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {officeBearers.length > 0 ? (
                        officeBearers.map((bearer, index) => (
                          <tr key={bearer.id}>
                            <td>
                              <div style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                backgroundImage: `url(${getResourceUrl(bearer.image_url)})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: '#e9ecef',
                                backgroundRepeat: 'no-repeat'
                              }}></div>
                            </td>
                            <td>{bearer.name}</td>
                            <td>{bearer.position}</td>
                            <td>
                              {bearer.term_start ? (
                                <>
                                  {formatDate(bearer.term_start)} 
                                  {bearer.term_end ? ' - ' + formatDate(bearer.term_end) : ' - Present'}
                                </>
                              ) : 'N/A'}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  onClick={() => handleChangeOrder(bearer.id, 'up')}
                                  className="admin-table-btn"
                                  disabled={index === 0}
                                  title="Move up"
                                >
                                  <FontAwesomeIcon icon={faArrowUp} />
                                </button>
                                <button
                                  onClick={() => handleChangeOrder(bearer.id, 'down')}
                                  className="admin-table-btn"
                                  disabled={index === officeBearers.length - 1}
                                  title="Move down"
                                >
                                  <FontAwesomeIcon icon={faArrowDown} />
                                </button>
                              </div>
                            </td>
                            <td>
                              <div className="admin-table-actions">
                                <Link 
                                  to={`/admin/office-bearers/${bearer.id}`} 
                                  className="admin-table-btn admin-table-btn-view"
                                  title="View office bearer details"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </Link>
                                <Link 
                                  to={`/admin/office-bearers/${bearer.id}/edit`} 
                                  className="admin-table-btn admin-table-btn-edit"
                                  title="Edit office bearer"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Link>
                                <button 
                                  className="admin-table-btn admin-table-btn-delete"
                                  title="Delete office bearer"
                                  onClick={() => handleDelete(bearer)}
                                >
                                  <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                            No office bearers found matching your criteria
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '500px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Confirm Deletion</h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete <strong>{bearerToDelete?.name}</strong> from office bearers?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default OfficeBearersList;
